import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { Linkify } from '@/components/Linkify/Linkify';
import { isArabic } from '@/config/constants';
import { useMarkdownToHtml } from '@/hooks/useMarkdownToHtml';
const ANIMATION_DURATION_MS = 500;
/**
 * Count total text characters in a DOM tree (excludes tags/attributes).
 * Used to calculate per-character delay so the full animation takes ~ANIMATION_DURATION_MS.
 */
function countTextChars(node) {
    var _a, _b;
    if (node.nodeType === Node.TEXT_NODE) {
        return (_b = (_a = node.textContent) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    }
    let count = 0;
    node.childNodes.forEach((child) => {
        count += countTextChars(child);
    });
    return count;
}
/**
 * Walk a parsed DOM tree: create elements instantly, type text content char by char.
 * This avoids showing raw markdown/HTML tags during the animation.
 */
async function typeNode(source, target, delay, signal) {
    var _a;
    for (const child of Array.from(source.childNodes)) {
        if (signal.aborted)
            return;
        if (child.nodeType === Node.TEXT_NODE) {
            const text = (_a = child.textContent) !== null && _a !== void 0 ? _a : '';
            const textNode = document.createTextNode('');
            target.appendChild(textNode);
            for (const char of text) {
                if (signal.aborted)
                    return;
                textNode.textContent += char;
                if (delay > 0) {
                    await new Promise((resolve) => {
                        const timer = setTimeout(resolve, delay);
                        signal.addEventListener('abort', () => {
                            clearTimeout(timer);
                            resolve();
                        }, { once: true });
                    });
                }
            }
        }
        else if (child.nodeType === Node.ELEMENT_NODE) {
            const el = child;
            const clone = document.createElement(el.tagName);
            for (const attr of Array.from(el.attributes)) {
                clone.setAttribute(attr.name, attr.value);
            }
            target.appendChild(clone);
            await typeNode(child, clone, delay, signal);
        }
    }
}
export const Text = ({ message, event, showAnimation, onClick }) => {
    var _a;
    const text = message ? message.text : ((_a = event.body) === null || _a === void 0 ? void 0 : _a.text) || event.text || '';
    const { html } = useMarkdownToHtml(text);
    const containerRef = useRef(null);
    const animationRef = useRef(null);
    // Typing animation: parse HTML into DOM, then walk the tree
    useEffect(() => {
        if (!showAnimation || !html || !animationRef.current)
            return;
        const container = animationRef.current;
        container.innerHTML = '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const totalChars = countTextChars(doc.body);
        const charDelay = totalChars > 0
            ? Math.max(1, Math.floor(ANIMATION_DURATION_MS / totalChars))
            : 0;
        const abortController = new AbortController();
        typeNode(doc.body, container, charDelay, abortController.signal);
        return () => {
            abortController.abort();
        };
    }, [html, showAnimation]);
    const handleClick = useCallback((e) => {
        if (!onClick)
            return;
        const target = e.target;
        if (target.tagName === 'A') {
            onClick({
                type: 'url',
                label: target.textContent || target.getAttribute('href') || '',
                value: target.getAttribute('href') || '',
            });
        }
    }, [onClick]);
    return (_jsx("span", { className: "markdown w-full leading-6 text-foreground [&_a>*]:pointer-events-none [&_p]:max-w-md", style: { fontSize: 'var(--font-size-brain-message)' }, dir: isArabic(text), ref: containerRef, onClick: handleClick, onKeyDown: (e) => e.key === 'Enter' && handleClick(e), role: "presentation", children: _jsx("div", { className: "animate-fade-in", ref: animationRef, children: !showAnimation && _jsx(Linkify, { children: html }) }) }));
};
